import { useMemo, useState } from "react";
import { Trash2, Plus } from "lucide-react";

/**
 * In-place retail-price editor for a product's price_table.
 * Handles all 3 strategies: tiered, booklet, tiered_total.
 * UPS Store price auto = retail × 0.60 (computed, not editable).
 *
 * Props:
 *  - product: the product object with `price_strategy` and `price_table`
 *  - onChange(newPriceTable): called on every edit; parent stores it
 */
const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const PriceInput = ({ value, onChange, testid }) => (
  <input
    type="number"
    step="0.01"
    min="0"
    className="input-swiss w-24 px-2 py-1 rounded-sm font-mono text-[13px] text-right"
    value={value ?? 0}
    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    data-testid={testid}
  />
);

const ModifierInput = ({ label, value, onChange, hint, testid }) => (
  <div className="flex items-center gap-3 py-1">
    <div className="flex-1">
      <div className="text-xs font-bold uppercase tracking-[0.12em]">{label}</div>
      {hint && <div className="text-[11px] font-mono text-[color:var(--ups-subtext)]">{hint}</div>}
    </div>
    <input
      type="number"
      step="0.01"
      className="input-swiss w-24 px-2 py-1 rounded-sm font-mono text-right"
      value={value ?? 0}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      data-testid={testid}
    />
  </div>
);

const PricingTableEditor = ({ product, onChange }) => {
  const strategy = product?.price_strategy || "formula";
  const table = product?.price_table || {};
  const rows = table.rows || [];
  const modifiers = table.modifiers || {};
  const [expandedIdx, setExpandedIdx] = useState(new Set(rows.map((_, i) => i)));

  if (strategy === "formula") {
    return (
      <div
        className="text-sm p-4"
        style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}
      >
        This product uses the <b>formula</b> pricing strategy (paper unit price × size multiplier × quantity).
        Edit the paper prices on the <b>Papers</b> tab and size multipliers on the <b>Sizes</b> tab.
      </div>
    );
  }

  const updateRowTier = (rowIdx, tierIdx, key, value) => {
    const newRows = rows.map((r, i) => {
      if (i !== rowIdx) return r;
      const newTiers = r.tiers.map((t, j) => (j === tierIdx ? { ...t, [key]: value } : t));
      return { ...r, tiers: newTiers };
    });
    onChange({ ...table, rows: newRows });
  };

  const updateModifier = (path, value) => {
    const newMods = { ...modifiers, ...path(modifiers, value) };
    onChange({ ...table, modifiers: newMods });
  };

  const setScalar = (key) => (v) =>
    updateModifier(() => ({ [key]: v }), v);

  const setMapEntry = (key, entryKey) => (v) =>
    updateModifier((m) => ({ [key]: { ...(m[key] || {}), [entryKey]: v } }), v);

  const removeMapEntry = (key, entryKey) => {
    const newMap = { ...(modifiers[key] || {}) };
    delete newMap[entryKey];
    onChange({ ...table, modifiers: { ...modifiers, [key]: newMap } });
  };

  const addMapEntry = (key, promptText) => {
    const k = window.prompt(promptText);
    if (!k) return;
    const v = parseFloat(window.prompt("Value (number):") || "0") || 0;
    updateModifier((m) => ({ [key]: { ...(m[key] || {}), [k]: v } }), v);
  };

  const toggleExpanded = (idx) => {
    const next = new Set(expandedIdx);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setExpandedIdx(next);
  };

  return (
    <div className="space-y-5" data-testid="pricing-table-editor">
      {/* Modifiers section */}
      <div
        className="p-4 rounded-sm"
        style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}
      >
        <div className="overline mb-3">Modifiers</div>
        {strategy === "tiered" && (
          <>
            <ModifierInput
              label="Single-sided multiplier"
              hint="Applied when only single_sided finish is selected. Typical: 0.5 (Flyers), 0.75 (Postcards)."
              value={modifiers.single_side_multiplier ?? 1}
              onChange={setScalar("single_side_multiplier")}
              testid="mod-single-side"
            />
            <ModifierInput
              label="Full bleed multiplier"
              hint="Applied when 'full_bleed' finish is selected. Typical: 1.5 (+50%)."
              value={modifiers.full_bleed_multiplier ?? 1}
              onChange={setScalar("full_bleed_multiplier")}
              testid="mod-full-bleed"
            />
          </>
        )}
        {strategy === "booklet" && (
          <ModifierInput
            label="Full bleed multiplier"
            hint="Applied when 'full_bleed' finish is selected. Typical: 1.5."
            value={modifiers.full_bleed_multiplier ?? 1}
            onChange={setScalar("full_bleed_multiplier")}
            testid="mod-full-bleed"
          />
        )}

        {/* size_multipliers map */}
        {(strategy === "tiered" || strategy === "booklet") && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-[0.12em]">Size multipliers</div>
              <button
                type="button"
                onClick={() => addMapEntry("size_multipliers", "Size id (e.g. 5.5x8.5):")}
                className="text-[11px] font-bold uppercase tracking-[0.12em] flex items-center gap-1 px-2 py-1 hover:bg-white rounded-sm"
                data-testid="add-size-modifier"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            {Object.entries(modifiers.size_multipliers || {}).length === 0 && (
              <div className="text-[11px] text-[color:var(--ups-subtext)] font-mono">none</div>
            )}
            {Object.entries(modifiers.size_multipliers || {}).map(([sid, mult]) => (
              <div key={sid} className="flex items-center gap-2 py-1">
                <span className="font-mono text-xs flex-1">{sid}</span>
                <PriceInput
                  value={mult}
                  onChange={setMapEntry("size_multipliers", sid)}
                  testid={`size-mult-${sid}`}
                />
                <button
                  onClick={() => removeMapEntry("size_multipliers", sid)}
                  className="p-1 hover:bg-red-100 rounded-sm text-red-700"
                  aria-label="remove"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* paper_upgrade_pct map */}
        {strategy === "tiered" && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-[0.12em]">
                Paper upgrade % (e.g. 0.25 = +25%)
              </div>
              <button
                type="button"
                onClick={() => addMapEntry("paper_upgrade_pct", "Paper id (e.g. 16pt_gloss_cover):")}
                className="text-[11px] font-bold uppercase tracking-[0.12em] flex items-center gap-1 px-2 py-1 hover:bg-white rounded-sm"
                data-testid="add-paper-upgrade"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            {Object.entries(modifiers.paper_upgrade_pct || {}).length === 0 && (
              <div className="text-[11px] text-[color:var(--ups-subtext)] font-mono">none</div>
            )}
            {Object.entries(modifiers.paper_upgrade_pct || {}).map(([pid, pct]) => (
              <div key={pid} className="flex items-center gap-2 py-1">
                <span className="font-mono text-xs flex-1">{pid}</span>
                <PriceInput
                  value={pct}
                  onChange={setMapEntry("paper_upgrade_pct", pid)}
                  testid={`paper-upgrade-${pid}`}
                />
                <button
                  onClick={() => removeMapEntry("paper_upgrade_pct", pid)}
                  className="p-1 hover:bg-red-100 rounded-sm text-red-700"
                  aria-label="remove"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rows */}
      {rows.map((row, rowIdx) => {
        const match = row.match || {};
        const label = [
          match.size_id && `size: ${match.size_id}`,
          match.paper_id && `paper: ${match.paper_id}`,
          (match.required_finishes || []).length > 0 &&
            `finishes: ${(match.required_finishes || []).join(",")}`,
        ]
          .filter(Boolean)
          .join("  ·  ") || "(match any)";
        const expanded = expandedIdx.has(rowIdx);
        return (
          <div
            key={rowIdx}
            className="card-swiss rounded-sm overflow-hidden"
            data-testid={`price-row-${rowIdx}`}
          >
            <button
              type="button"
              onClick={() => toggleExpanded(rowIdx)}
              className="w-full text-left px-4 py-3 font-mono text-sm flex items-center justify-between hover:bg-[color:var(--ups-muted)] transition"
              style={{ borderBottom: expanded ? "1px solid var(--ups-border)" : "none" }}
            >
              <span className="font-bold">{label}</span>
              <span className="text-[11px] text-[color:var(--ups-subtext)]">
                {expanded ? "collapse" : "expand"}
              </span>
            </button>
            {expanded && (
              <div className="p-4">
                {strategy === "tiered" && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase font-bold tracking-[0.12em] text-[color:var(--ups-subtext)]">
                        <th className="text-left pb-2">Qty range</th>
                        <th className="text-right pb-2">Retail / unit</th>
                        <th className="text-right pb-2">UPS / unit (40% off)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(row.tiers || []).map((t, tierIdx) => (
                        <tr key={tierIdx}>
                          <td className="py-1 font-mono text-xs">
                            {t.min?.toLocaleString()} – {t.max?.toLocaleString()}
                          </td>
                          <td className="py-1 text-right">
                            <PriceInput
                              value={t.unit_price}
                              onChange={(v) => updateRowTier(rowIdx, tierIdx, "unit_price", v)}
                              testid={`tier-unit-${rowIdx}-${tierIdx}`}
                            />
                          </td>
                          <td className="py-1 text-right font-mono text-[13px]" style={{ color: "var(--ups-accent)" }}>
                            {fmtUSD(Number(t.unit_price || 0) * 0.6)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {strategy === "booklet" && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase font-bold tracking-[0.12em] text-[color:var(--ups-subtext)]">
                        <th className="text-left pb-2">Qty range</th>
                        <th className="text-right pb-2">Base 4-page</th>
                        <th className="text-right pb-2">Each extra 4-page</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(row.tiers || []).map((t, tierIdx) => (
                        <tr key={tierIdx}>
                          <td className="py-1 font-mono text-xs">
                            {t.min?.toLocaleString()} – {t.max?.toLocaleString()}
                          </td>
                          <td className="py-1 text-right">
                            <PriceInput
                              value={t.base_4page_price}
                              onChange={(v) => updateRowTier(rowIdx, tierIdx, "base_4page_price", v)}
                              testid={`tier-base4-${rowIdx}-${tierIdx}`}
                            />
                          </td>
                          <td className="py-1 text-right">
                            <PriceInput
                              value={t.extra_4page_price}
                              onChange={(v) => updateRowTier(rowIdx, tierIdx, "extra_4page_price", v)}
                              testid={`tier-extra4-${rowIdx}-${tierIdx}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {strategy === "tiered_total" && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase font-bold tracking-[0.12em] text-[color:var(--ups-subtext)]">
                        <th className="text-left pb-2">Qty range</th>
                        <th className="text-right pb-2">Total retail price</th>
                        <th className="text-right pb-2">Total UPS (40% off)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(row.tiers || []).map((t, tierIdx) => (
                        <tr key={tierIdx}>
                          <td className="py-1 font-mono text-xs">
                            {t.min?.toLocaleString()} – {t.max?.toLocaleString()}
                          </td>
                          <td className="py-1 text-right">
                            <PriceInput
                              value={t.total_price}
                              onChange={(v) => updateRowTier(rowIdx, tierIdx, "total_price", v)}
                              testid={`tier-total-${rowIdx}-${tierIdx}`}
                            />
                          </td>
                          <td className="py-1 text-right font-mono text-[13px]" style={{ color: "var(--ups-accent)" }}>
                            {fmtUSD(Number(t.total_price || 0) * 0.6)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}

      <p className="text-[11px] font-mono text-[color:var(--ups-subtext)]">
        Enter retail prices only. UPS Store prices auto-compute at <b>40% off</b> (retail × 0.60).
      </p>
    </div>
  );
};

export default PricingTableEditor;
